import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AdminEventModel } from '../shared/models/admin-event-models';
import * as XLSX from 'xlsx';
import { ColDef, GridOptions } from 'ag-grid-community';
import { DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AdminEventService } from '../admin-event/services/admin-event.service';
import { forkJoin } from 'rxjs';

const EXCEL_COLS = {
  bookingDate: '__EMPTY',
  startTime: '__EMPTY_2',
  endTime: '__EMPTY_3',
  eventTitle: '__EMPTY_4',
  location: '__EMPTY_5'
} as const;

type ExcelCols = typeof EXCEL_COLS;
type ExcelKey = ExcelCols[keyof ExcelCols];
type RawExcelRow = Record<ExcelKey | string, unknown>;

@Component({
    selector: 'app-admin-event-imports',
    templateUrl: './admin-event-imports.component.html',
    styleUrls: ['./admin-event-imports.component.scss'],
    standalone: false
})
export class AdminEventImportsComponent {
  private readonly datePipe = new DatePipe('en-US');

  @Output() eventsProcessed: EventEmitter<void> = new EventEmitter<void>();

  faTimes = faTimes;
  form: FormGroup;
  adminEvents: AdminEventModel[] = [];

  columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'date',
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return this.datePipe.transform(date, 'EEEE MMMM d') || '';
      },
      resizable: false
    },
    {
      headerName: 'Start Time',
      flex: 1,
      field: 'date',
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return this.datePipe.transform(date, 'hh:mm a') || '';
      },
      resizable: false
    },
    {
      headerName: 'End Time',
      flex: 1,
      field: 'endDate',
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return this.datePipe.transform(date, 'hh:mm a') || '';
      },
      resizable: false
    },
    {
      headerName: 'Event Title',
      field: 'eventTitle',
      flex: 3,
      resizable: false
    },
    {
      headerName: 'Location',
      field: 'location',
      flex: 1,
      resizable: false
    }
  ];

  gridOptions: GridOptions = {
    columnDefs: this.columnDefs,
    headerHeight: 42,
    rowHeight: 45
  }

  constructor(
    private fb: FormBuilder,
    public modal: NgbActiveModal,
    public toastr: ToastrService,
    private service: AdminEventService) {
    this.form = this.fb.group({
      file: ['']
    });
  }

  //parse each row using xlsx library
  processExcel(event: any): void {
    if (this.adminEvents.length > 0) {
      this.clearAll();
    }

    const file = event.target.files[0];
    if (!file) {
      this.toastr.warning('Please select a file');
      return;
    }

    const reader = new FileReader();
    reader.onerror = (error) => {
      return this.toastr.warning('The file could not be parsed. Please try again.');
    };

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true });

        this.processEventsJsonData(jsonData as RawExcelRow[]);
      } catch (error) {
        this.toastr.warning('The file could not be parsed. Please try again.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  submitImportedEvents(): void {
    if (this.adminEvents.length === 0) {
      return;
    }

    const requests = this.adminEvents.map(adminEvent => this.service.createEvent(adminEvent));

    forkJoin(requests).subscribe({
      next: () => {
        this.modal.dismiss();
        this.toastr.success('Events Have Been Processed Successfully');
        this.eventsProcessed.emit();
      },
      error: (error) => {
        console.error('Error creating events:', error);
        this.toastr.error('There was an error processing events');
      }
    });
  }

  private processEventsJsonData(jsonData: RawExcelRow[]): void {
    const events = jsonData
      .filter(row => this.isRowValidEventJson(row))
      .map(row => this.convertJsonToModel(row))
      .filter(row => !!row.date); //filter out any row that do not have event date, do not process blank excel rows that result in error

    this.adminEvents = events;
  }

  private isRowValidEventJson(jsonRow: RawExcelRow): boolean {
    // Validate that row starts with a date (dates are stored in Excel as a number)
    const rowFirstValue = jsonRow[EXCEL_COLS.bookingDate];
    return typeof rowFirstValue === 'number' && !isNaN(rowFirstValue);
  }

  // Converts an excel value for a date (the number of days since 1900-01-01) to a js Date object
  private excelSerialToDate(serial: number): Date {
    const excelEpoch = new Date(1899, 11, 30);
    return new Date(excelEpoch.getTime() + serial * 86400 * 1000);
  }

  private parseExcelTimeToHoursMinutes(
    value: unknown
  ): { hours: number; minutes: number; } | null {
    // If stored as a date in excel
    if (value instanceof Date && !isNaN(value.getTime())) {
      return { hours: value.getHours(), minutes: value.getMinutes() };
    }

    // If stored as fraction of a day
    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.excelFractionToTime(value);
    }

    // If stored as a string like "1:00 PM", "9:15 AM"
    if (typeof value === 'string') {
      const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
      if (!match) return null;

      let hours = Number(match[1]);
      const minutes = Number(match[2]);
      const ampm = match[3].toUpperCase();

      if (hours < 1 || hours > 12) return null;
      if (minutes < 0 || minutes > 59) return null;

      if (ampm === 'AM') hours = hours === 12 ? 0 : hours;
      else hours = hours === 12 ? 12 : hours + 12;

      return { hours, minutes };
    }

    return null;
  }

  // Converts an excel value for a time (a fraction of the time passed in a day)
  private excelFractionToTime(fraction: number) {
    const totalSeconds = Math.round(fraction * 24 * 60 * 60);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    return { hours, minutes };
  }

  private padStart2(n: number): string {
    return n.toString().padStart(2, '0');
  }

  private buildDateTimeString(date: Date, timeRaw: unknown): string | null {
    const base = this.datePipe.transform(date, 'yyyy-MM-dd');
    if (!base) return null;

    const time = this.parseExcelTimeToHoursMinutes(timeRaw);
    if (!time) return null;

    const { hours, minutes } = time;
    return `${base} ${this.padStart2(hours)}:${this.padStart2(minutes)}:00.000`;
  }

  private convertJsonToModel(jsonRow: RawExcelRow): AdminEventModel {
    const bookingDateSerial = jsonRow[EXCEL_COLS.bookingDate] as number;
    const bookingDate = this.excelSerialToDate(bookingDateSerial);

    const startRaw = jsonRow[EXCEL_COLS.startTime];
    const endRaw = jsonRow[EXCEL_COLS.endTime];


    const startDateTime = this.buildDateTimeString(bookingDate, startRaw);
    const endDateTime = this.buildDateTimeString(bookingDate, endRaw);

    const adminEvent: AdminEventModel = {
      id: 0,
      date: startDateTime ?? null,
      endDate: endDateTime ?? null,
      eventTitle: (jsonRow['__EMPTY_4'] as string) ?? '',
      location: (jsonRow['__EMPTY_5'] as string) ?? '',
      eventType: null,
    }

    return adminEvent;
  }

  private clearAll() {
    this.form.get('file')?.setValue(null);
    this.adminEvents = [];
  }
}
